import { hasValidEmployeeSessionFor } from "src/utils/employeeSessionValid";
import NavBar from "./NavBar";
import MesaLeaseBridge from "src/components/mesaLease/MesaLeaseBridge";
import TopBar from "./TopBar";
import PropTypes from "prop-types";
import React, { Fragment, useEffect, useState } from "react";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useHubUsers from "src/utils/hubs/HubUser";
import {
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@material-ui/core";
import ErrorComponentBoundry from "src/utils/ComponentErrorBoundry";
import ModalEmployee from "./TopBar/ModalEmployee";
import { inactivityLockSeconds, shouldLockOnInactivity } from "src/utils/inactivityLock";
import { lockOnInactivityIfEnabled } from "src/utils/inactivityLockFlow";
import {
  decideDeviceEnforcementOnMount,
  hasDecidedDeviceEnforcement,
  markDeviceEnforcementDecided,
} from "src/utils/deviceEnforcementBoot";
import { useDispatch, useSelector } from "src/redux/store";
import useAuth from "src/hooks/useAuth";
import { deleteUserSession } from "src/redux/slices/authJwt";
import useHubLogs from "src/utils/hubs/HubLogs";
import useHubWorkerStatus from "src/utils/hubs/HubWorkerStatus";
import { fetchWorkerStatus } from "src/redux/slices/workerStatus";
import { fetchActiveIntegrationCodes } from "src/redux/slices/integrations";
import { getActiveAddOns, getAddOns, setAddOnQuantity } from "src/redux/slices/addOns";
import SubscriptionAlertBanner, {
  selectHasSubscriptionAlert,
} from "./SubscriptionAlertBanner";
import LegalOnboardingGate from "./LegalOnboardingGate";
import WhatsAppQuotaBanner from "./WhatsAppQuotaBanner";
import AddOnContractDialog from "./AddOnContractDialog";
import useNetworkStatus from "src/hooks/useNetworkStatus";
import { DialogAnimate } from "src/components/Animate";
import { setHaveErrorLogs } from "src/redux/slices/logs";
import useResponsive from "src/hooks/useResponsive";
import trashFill from "@iconify-icons/eva/trash-outline";
import { fDateTimeSuffixJustDate, getArqueoDayLabel } from "src/utils/formatTime";
import { LoadingButton } from "@material-ui/lab";
import axios from "src/utils/axios";
import { useSnackbar } from "notistack";
import Icon from "@iconify/react";
import Label from "src/components/Label";
import MyAutoSnack from "src/components/MyAutoSnack";
import { PATH_APP } from "src/routes/paths";
import { useHistory } from "react-router-dom/cjs/react-router-dom";
import {
  openArqueoDialogSuccess,
  startArqueoSuccess,
} from "src/redux/slices/caja";
import { FormControl, InputLabel, MenuItem, Select, Grid } from "@mui/material";
import PopUpInputReferenceWithoutDialog from "src/components/PopUps/PopUpInputReferenceWithoutDialog";
import useBackgroundSyncDetect from "./UseBackgroundSyncDetect";
import TpvRealtimeHubs from "src/realtime/TpvRealtimeHubs";
import useUserSessionHub from "src/utils/hubs/PrivateUserSessionHub";
import useInactivity from "src/hooks/useInactivity";
import {
  changeOpenEmployeeModal,
  getCurrentClockingSuccess,
  openKiosk,
  setEmployee,
} from "src/redux/slices/employee";
import KioskOverlay from "./KioskOverlay";
import HelpLauncher from "src/help/ui/HelpLauncher";
import PendingSignaturesModal from "./PendingSignaturesModal";
import useEmployeeRole from "src/hooks/useEmployeeRole";
import { issueEmployeeSessionWithoutPin } from "src/redux/slices/employeeSession";
import { getDeviceId } from "src/utils/deviceId";
import EmployeeShiftTracker from "src/views/app/UserNormal/employees/EmployeeClockings/EmployeeShiftTracker";
import PostCheckoutAutoApply from "src/components/onboarding/PostCheckoutAutoApply";
import { MIconButton } from "src/theme";
import closeFill from "@iconify/icons-eva/close-fill";
import { useClaims } from "src/hooks/useClaims";
import DeviceCommandsBridge from "src/components/DeviceCommandsBridge";
import NewVersionBanner from "src/components/NewVersionBanner";
import { openClockingIfDue } from "src/utils/clocking/clockingDue";

// ----------------------------------------------------------------------

const APP_BAR_MOBILE = 34;
const APP_BAR_DESKTOP = 24;
/** Alto individual de cada banner de estado (worker offline, impresora con incidencia). */
export const STATUS_BANNER_HEIGHT = 40;

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up("sm")]: {
      display: "flex",
      minHeight: "100%",
      overflow: "hidden",
    },
    [theme.breakpoints.down("sm")]: {
      height: "100%",
    },
  },
  // main aplica el paddingTop base. El extra por banners se inyecta inline
  // desde el componente (n banners visibles × STATUS_BANNER_HEIGHT).
  main: {
    [theme.breakpoints.up("lg")]: {
      paddingTop: APP_BAR_DESKTOP + 40,
    },
    [theme.breakpoints.down("sm")]: {
      paddingTop: theme.spacing(10),
      height: "100%",
    },
    [theme.breakpoints.up("sm")]: {
      flexGrow: 1,
      overflow: "auto",
      minHeight: "100%",
      paddingTop: APP_BAR_MOBILE + 40,
    },
  },
}));

// ----------------------------------------------------------------------

DashboardLayout.propTypes = {
  children: PropTypes.node,
};

function DashboardLayout({ children }) {
  useBackgroundSyncDetect();
  const { accessCaja, allowMakeArqueo } = useEmployeeRole();

  useHubLogs();
  useHubUsers();
  // Fase 4 · mesas/reservas/pedidos: `<TpvRealtimeHubs />` (una conexión
  // `/hub/tpv` con V2, o los hubs legacy con el kill-switch).
  useUserSessionHub();
  // Suscripción global al hub de workers: así el banner rojo aparece en cualquier
  // pantalla del TPV (no solo en el panel de impresión) cuando un worker cae.
  useHubWorkerStatus();
  // Escucha eventos online/offline del navegador + Network Information API.
  // La latencia real la mide el interceptor de axios (src/utils/axios.js).
  useNetworkStatus();
  // Fase 5 · Los hooks del monitor (conectividad, persistencia del API
  // monitor y toggle remoto) se montan una sola vez en <DebugInstrumentation/>
  // (App.js) con el `source` real de cada ruta, también en el TPV camarero.

  const classes = useStyles();
  const theme = useTheme();
  const dispatch = useDispatch();
  const history = useHistory();
  const width = useResponsive("down", "sm");
  const [open, setOpen] = useState(false);
  const [openSession, setOpenSession] = useState(false);

  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);

  const { isAdmin, isEmployeeWebSession } = useClaims();
  const { user, logout, isAuthenticated } = useAuth();
  const { haveErrorLogs } = useSelector((state) => state.logs);
  const { haveMoreSessionsThanAllowed, userSessions: allUserSessions } = useSelector(
    (state) => state.authJwt,
  );
  // El slice ahora incluye la sesión ACTUAL en el array (para que
  // componentes como ClockingDeviceCard puedan encontrarla). Aquí solo
  // queremos las OTRAS sesiones — el user no debería poder cerrarse a
  // sí mismo desde este dialog (le echaría del TPV).
  const currentSid = window.localStorage?.getItem("sessionIdentifier");
  const userSessions = React.useMemo(
    () => (allUserSessions || []).filter((s) => s.userSessionIdentifier !== currentSid),
    [allUserSessions, currentSid],
  );
  // Catálogo de add-ons para el shortcut "Contratar dispositivo extra"
  // del dialog de sesiones. Si no hay uno de tipo "device" configurado
  // (Stripe id vacío), el botón se oculta y solo se pide cerrar
  // sesiones — comportamiento seguro (no proponer al user contratar
  // algo que aún no está en el catálogo).
  const { addOns, userAddOns } = useSelector((state) => state.addOns);
  const [buyingDevice, setBuyingDevice] = React.useState(false);
  const {
    openEmployeeModal,
    currentActiveEmployee,
    openClockingModal,
    clockings,
    kioskOpen,
    pendingSignatures,
    employees,
  } = useSelector((state) => state.employees);
  const { todayArqueo, openArqueoDialog, arqueos, arqueo, lastArqueoDay } =
    useSelector((state) => state.caja);

  // "Arqueo obligatorio del día anterior" — cuando el usuario activa
  // `RequirePreviousDayArqueo` en Ajustes y queda pendiente el arqueo
  // del día previo (no marcado como cerrado), forzamos la apertura del
  // modal de arqueos aunque el arqueo de hoy ya esté abierto. Esto
  // bloquea la operativa del TPV hasta que el usuario cierre el
  // pasado. Toggle OFF = comportamiento actual intacto.
  const hasPendingPrevArqueo = React.useMemo(
    () =>
      !!(
        user?.requirePreviousDayArqueo &&
        lastArqueoDay &&
        !lastArqueoDay.isClose
      ),
    [user?.requirePreviousDayArqueo, lastArqueoDay],
  );

  const [selectedArqueo, setSelectedArqueo] = useState(todayArqueo?.id || null);

  const { enqueueSnackbar, closeSnackbar } = useSnackbar();

  const [openNav, setOpenNav] = useState(false);

  const [step, setStep] = useState("OpenCaja");

  // Banners de estado del sistema — se apilan debajo del TopBar. Cada uno añade
  // STATUS_BANNER_HEIGHT al paddingTop del main para que el contenido no quede tapado.
  // 2026-10-08 · Worker e impresoras ya no van aquí: avisan en la pastilla de
  // estado del TopBar (TpvStatusPill), sin tapar el TPV ni bajar el contenido.
  // Solo queda la suscripción (facturación, no operativa del TPV).
  const hasSubscriptionAlert = useSelector(selectHasSubscriptionAlert);
  const visibleBanners = hasSubscriptionAlert ? 1 : 0;
  const extraPaddingTop = visibleBanners * STATUS_BANNER_HEIGHT;

  // 2026-10-18 · null = bloqueo desactivado (ver src/utils/inactivityLock.js).
  const isInactivity = useInactivity(inactivityLockSeconds(user));

  // Rehidratar currentClocking desde clockings persistido tras un reload
  useEffect(() => {
    if (
      currentActiveEmployee &&
      currentActiveEmployee !== 0 &&
      clockings?.length > 0
    ) {
      dispatch(getCurrentClockingSuccess(currentActiveEmployee));
    }
  }, []);

  // Fetch inicial de workers al montar el layout — así el banner conoce su estado
  // antes de que llegue el primer push SignalR.
  useEffect(() => {
    if (isAuthenticated && !isAdmin) dispatch(fetchWorkerStatus());
  }, [dispatch, isAuthenticated]);

  // Fix6 2026-07-22 · códigos de integraciones activas. Los usa el nav
  // para decidir si mostrar items dependientes ("Reseñas · PRO" solo si
  // Google está conectado). Fire-and-forget silencioso.
  useEffect(() => {
    if (isAuthenticated && !isAdmin) {
      dispatch(fetchActiveIntegrationCodes());
      dispatch(getActiveAddOns());
    }
  }, [dispatch, isAuthenticated]);

  useEffect(() => {
    if (openEmployeeModal && openEmployeeModal?.isOpen)
      setOpen(openEmployeeModal);
  }, [openEmployeeModal]);

  useEffect(() => {
    if (todayArqueo) setSelectedArqueo(todayArqueo);
  }, [todayArqueo]);

  useEffect(() => {
    if (haveMoreSessionsThanAllowed) setOpenSession(true);
  }, [haveMoreSessionsThanAllowed]);

  useEffect(() => {
    // Cargamos el catálogo si aún no está — necesario para el shortcut
    // "Contratar dispositivo extra" del dialog de sesiones.
    if (isAuthenticated && (!addOns || addOns.length === 0)) {
      dispatch(getAddOns(true));
    }
  }, [dispatch, isAuthenticated, addOns]);

  useEffect(() => {
    if (openArqueoDialog) {
      setSelectedArqueo(todayArqueo);
    }
  }, [openArqueoDialog]);

  // Fuerza abrir el modal si hay arqueo anterior pendiente y el toggle
  // está activo. Se dispara en cuanto cambia `hasPendingPrevArqueo`
  // (por ej. al terminar el fetch de `getCurrentCaja`). Sin esto, el
  // modal solo aparecería si el arqueo de HOY también estuviera
  // cerrado — pero puede estar abierto y aun así deber cerrar ayer.
  useEffect(() => {
    if (hasPendingPrevArqueo) dispatch(openArqueoDialogSuccess(true));
  }, [hasPendingPrevArqueo, dispatch]);

  useEffect(() => {
    // Sesión personal del empleado (login web / QR personal): NO
    // debemos disparar el kiosko por inactividad — cambiar de usuario
    // no está permitido en ese flujo. Si es admin dueño con empleado
    // identificado, sí se mantiene el auto-lock del TPV compartido.
    if (
      shouldLockOnInactivity({
        user,
        isInactive: isInactivity,
        kioskOpen,
        currentActiveEmployee,
        isEmployeeWebSession,
      })
    ) {
      dispatch(lockOnInactivityIfEnabled());
    }
  }, [user, isInactivity, kioskOpen, isEmployeeWebSession, currentActiveEmployee]);

  // F-device-enforcement · 2026-08-24
  // Aplica los flags que dejó `useDeviceEnforcementHub` en localStorage
  // ANTES de este reload. Casos:
  //   • `assignedEmployeeId` presente → auto-identificar como ese
  //     empleado (setear UserEmployeeId + emitir JWT firmado si el
  //     empleado no tiene PIN). Sin esto, tras el reload el TPV
  //     seguía operando como admin porque nadie leía la asignación.
  //   • `deviceRequireEmployeeSelection=true` + sin empleado activo →
  //     abrir kiosko bloqueante para que se seleccione un empleado.
  // Se ejecuta 1 vez tras el mount cuando `authJwt` ya está listo. No
  // depende de `isAdmin` porque el enforcement aplica también cuando
  // el admin escanea el QR — es precisamente el caso de uso ("limitar
  // al admin que entra al TPV de mesa").
  useEffect(() => {
    if (!isAuthenticated) return;
    if (isEmployeeWebSession) return; // sesión personal del empleado, sin device flow
    let sessionKey = "";
    try { sessionKey = window.localStorage.getItem("sessionIdentifier") || ""; } catch { /* ignore */ }
    if (hasDecidedDeviceEnforcement(sessionKey)) return;
    try {
      const assignedIdRaw = window.localStorage.getItem("assignedEmployeeId");
      const requireSelection = window.localStorage.getItem("deviceRequireEmployeeSelection") === "true";

      const assignedId = assignedIdRaw ? Number(assignedIdRaw) : 0;
      const decision = decideDeviceEnforcementOnMount({
        assignedId,
        requireSelection,
        employees,
        currentActiveEmployee,
      });
      // Auto-identificar: setea header legacy siempre + intenta emitir
      // JWT firmado SOLO si sabemos que el empleado no tiene PIN. El
      // catálogo de `employees` puede llegar tarde tras el mount — si
      // aún no está, esperamos al siguiente render (no marcamos
      // la decisión como tomada hasta poder chequear el PIN).
      if (decision.action === "wait") return; // esperar a que hidrate el catálogo
      // 2026-10-18 · Decisión de arranque: se toma UNA vez por carga de
      // página y sesión web (no por montaje del layout). Sin esto, con
      // «exigir selección», pasarse al admin con PIN (currentActiveEmployee
      // → 0) reabría el kiosko — al momento o al volver de una mesa — y se
      // volvía a pedir el PIN.
      markDeviceEnforcementDecided(sessionKey);
      if (decision.action === "assign") {
        const assignedEmp = decision.employee;

        window.localStorage.setItem("UserEmployeeId", assignedId);
        try { axios.defaults.headers.common.UserEmployeeId = assignedId; } catch { /* ignore */ }
        dispatch(setEmployee(assignedId));
        dispatch(getCurrentClockingSuccess(assignedId));
        // Dispositivo asignado = identificación automática: si toca fichar
        // (entrada/salida) se abre una vez por turno y acción en esta pestaña,
        // no en cada recarga.
        openClockingIfDue({ dispatch, user, employee: assignedEmp, once: true });
        // Sin PIN → JWT firmado; con PIN → header legacy (el kiosko de
        // PIN se puede abrir aparte si el user quiere firmar). Antes se
        // llamaba a `issueWithoutPin` a lo bruto y el backend devolvía
        // 400 PIN_REQUIRED en cada mount del layout, ensuciando los
        // logs y disparando el globalErrorHandler.
        // Chequeamos ambas capitalizaciones porque el DTO puede llegar
        // con `password` (camelCase) o `Password` (PascalCase) según
        // configuración de Newtonsoft. El slice también tiene un guard
        // silencioso para el 400 PIN_REQUIRED como red de seguridad.
        const empPin = assignedEmp.password || assignedEmp.Password;
        // Tras recargar ya hay sesión válida de ese empleado (la restaura el
        // arranque): re-emitirla rotaba el token y cortaba las peticiones en
        // vuelo (tormenta de 401). Solo se emite si no la hay.
        if (!empPin && !hasValidEmployeeSessionFor(assignedId)) {
          dispatch(issueEmployeeSessionWithoutPin(assignedId, getDeviceId()))
            .catch(() => { /* fallback silencioso al header legacy */ });
        }
        return;
      }

      if (decision.action === "kiosk") {
        // Sin asignación pero require-selection → forzar kiosko manual
        // hasta que el user seleccione. `manual:true` = el user lo
        // abrió a propósito (evita heurísticas de auto-cierre).
        dispatch(openKiosk({ manual: true }));
      }
    } catch { /* nunca romper el layout por lectura de localStorage */ }
  }, [isAuthenticated, isEmployeeWebSession, currentActiveEmployee, employees, dispatch]);

  // 2026-09-14 · Retiradas las alertas de "empleado ha superado su
  // turno de Xh". Los clientes reportaron que interrumpen el flujo
  // del TPV (aparecen en medio de un cobro) y no aportan valor
  // accionable en ese momento — los desajustes horarios se ven en
  // el balance contractual del calendario. Ver historial git para
  // recuperar el snackbar si algún día se necesita opt-in.

  const closeSession = async (session) => {
    setLoading(true);
    setSelected(session.id);

    await axios
      .post(`/authentication/removeSession/${session.userSessionIdentifier}`)
      .then((res) => {
        MyAutoSnack(
          "success",
          "Sesión cerrada correctamente",
          enqueueSnackbar,
          closeSnackbar,
        );

        dispatch(deleteUserSession(session.id));
      })
      .catch((err) => {})
      .finally(() => {
        setLoading(false);
        setSelected(null);
      });
  };

  const RenderDialogAnimate = () => {
    // debe de mostrar un dialogo que diga que una de las impresoras contiene errores y que se debe de revisar
    return (
      <DialogAnimate
        open={haveErrorLogs}
        onClose={() => {
          dispatch(setHaveErrorLogs(false));
        }}
      >
        <DialogTitle>
          <Typography variant="h4">Error de impresora</Typography>
        </DialogTitle>

        <DialogContent>
          {/* <Typography variant="h5">
            <MLabel color="error">Impresora: </MLabel>
            <MLabel color="error">Cocina</MLabel>
          </Typography> */}

          <Typography variant="h6">
            Se ha detectado un error en una de las impresoras, que se debe de
            revisar y solucionar.
          </Typography>

          <Typography variant="h5">
            Tenga en cuenta que este error impedirá que se puedan imprimir las
            comandas, tickets y arqueos de caja
          </Typography>

          <Typography sx={{ mt: 2, ml: 2 }} variant="h6">
            El error puede ser debido a:
            <ul>
              <li>Impresora desconectada del tpv usb/wifi</li>
              <li>Impresora apagada</li>
              <li>Impresora sin conexión a internet</li>
              <li>Impresora sin papel</li>
            </ul>
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            color="info"
            fullWidth
            variant="contained"
            onClick={() => dispatch(setHaveErrorLogs(false))}
          >
            Revisar
          </Button>
        </DialogActions>
      </DialogAnimate>
    );
  };

  const RenderCloseActiveSessions = () => {
    return (
      <Dialog open={openSession && userSessions?.length > 0}>
        <DialogTitle>
          <Typography variant="h4">Cerrar sesiones activas</Typography>
        </DialogTitle>

        <DialogContent>
          {haveMoreSessionsThanAllowed ? (
            <Typography variant="h6">
              Se ha detectado que existen sesiones activas en otros
              dispositivos, por favor cierre las sesiones activas para poder
              continuar. Tenga en cuenta que el terminal cerrado no podrá
              realizar ninguna acción hasta que se vuelva a iniciar sesión.
            </Typography>
          ) : (
            <Typography variant="h6">
              Estas son las sesiones activas en otros dispositivos, si lo desea
              puede cerrarlas. Tenga en cuenta que el terminal cerrado no podrá
              realizar ninguna acción hasta que se vuelva a iniciar sesión.
            </Typography>
          )}

          <Grid
            container
            padding={2}
            spacing={2}
            justifyContent="center"
            alignContent="center"
            alignItems="center"
          >
            {userSessions?.map((row, i) => (
              <Grid item xs={12} sm={6} md={4}>
                <Card>
                  <Label
                    color="info"
                    sx={{
                      borderRadius: "0px 0px 0px 0px",
                      width: "100%",
                    }}
                  >
                    {row.name ? row.name : row.ip}
                  </Label>

                  <CardContent>
                    <Typography variant="body2">
                      {fDateTimeSuffixJustDate(row.creationDate)}
                    </Typography>
                  </CardContent>

                  <CardActions
                    disableSpacing
                    sx={{
                      justifyContent: "center",
                    }}
                  >
                    <LoadingButton
                      startIcon={
                        <Icon icon={trashFill} width={24} height={24} />
                      }
                      onClick={() => {
                        closeSession(row);
                      }}
                      pending={selected === row.id && loading}
                      disabled={selected !== row.id && loading}
                    ></LoadingButton>
                  </CardActions>
                </Card>
              </Grid>
            ))}
          </Grid>
        </DialogContent>

        <DialogActions>
          {(() => {
            // Shortcut "Contratar dispositivo extra" — solo si el catálogo
            // tiene un add-on de tipo "device" y hay sesión de Stripe activa.
            // Si el user no puede contratar (sin sub Stripe) mostramos solo
            // "Cerrar" para no llevarlo a un error.
            const deviceAddOn = (addOns || []).find(
              (a) => a.kind === "device" && a.isActive !== false && a.idStripe,
            );
            if (!haveMoreSessionsThanAllowed || !deviceAddOn) return null;
            const currentQty = (userAddOns || []).find((u) => u.idAddOn === deviceAddOn.id)?.quantity || 0;
            const label = `Contratar dispositivo extra (+${Number(deviceAddOn.price).toFixed(2)} €/mes)`;
            return (
              <LoadingButton
                color="primary"
                variant="outlined"
                loading={buyingDevice}
                onClick={async () => {
                  setBuyingDevice(true);
                  try {
                    await dispatch(setAddOnQuantity(deviceAddOn.id, currentQty + 1));
                    enqueueSnackbar("Dispositivo añadido — ya puedes seguir vendiendo", {
                      variant: "success",
                    });
                    setOpenSession(false);
                  } catch {
                    enqueueSnackbar("No se pudo añadir el dispositivo. Contacta con soporte.", {
                      variant: "error",
                    });
                  } finally {
                    setBuyingDevice(false);
                  }
                }}
              >
                {label}
              </LoadingButton>
            );
          })()}
          <Button
            color="warning"
            disabled={userSessions?.length >= user?.allowedSessions}
            variant="contained"
            onClick={() => setOpenSession(false)}
          >
            Cerrar
          </Button>

          {!haveMoreSessionsThanAllowed && (
            <Button
              color="success"
              variant="contained"
              onClick={() => setOpenSession(false)}
            >
              Continuar
            </Button>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const RenderArqueoCloseOpenDialog = () => {
    // Cuando el toggle "Arqueo obligatorio del día anterior" está ON y
    // hay pendiente, tomamos el control del dialog: no permitimos
    // cerrarlo (backdrop bloqueado) y mostramos SOLO el paso "cerrar
    // ayer" con CTA a la sección de arqueos. Al cerrar el arqueo
    // anterior desde ahí, `getCurrentCaja` refetchea y este flag pasa
    // a false → el dialog cae al flujo normal.
    if (hasPendingPrevArqueo) {
      const lastLabel = lastArqueoDay
        ? getArqueoDayLabel(lastArqueoDay.start, lastArqueoDay.end)
        : "el arqueo pendiente";
      return (
        <DialogAnimate
          onClose={() => {}}
          open={openArqueoDialog}
          width="sm"
          disableBackdropClick
          disableEscapeKeyDown
        >
          <DialogTitle>
            <Typography variant="h5" color="error">
              Cierra el arqueo del día anterior
            </Typography>
          </DialogTitle>
          <DialogContent>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Tienes activada la obligatoriedad de cerrar el arqueo del
              día anterior antes de operar. Cierra <strong>{lastLabel}</strong> para
              continuar con la venta de hoy.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Esta protección evita que un turno cierre sin cuadrar caja
              y descuadres se arrastren al día siguiente. Puedes
              desactivarla desde Ajustes → Caja.
            </Typography>
          </DialogContent>
          <DialogActions>
            <Button
              variant="contained"
              color="warning"
              size="large"
              onClick={() => {
                dispatch(openArqueoDialogSuccess(false));
                // Pasamos el id del arqueo pendiente para que la vista de
                // caja lo preseleccione en el desplegable "¿Cuándo?" y
                // muestre las ventas/desglose de ese día — sin este
                // querystring caía en el arqueo de HOY (default de la
                // vista) y el usuario tenía que buscarlo manualmente.
                const qs = lastArqueoDay?.id
                  ? `?arqueoId=${lastArqueoDay.id}`
                  : "";
                history.push(`${PATH_APP.tuCarta.caja}${qs}`);
              }}
            >
              IR A CERRAR ARQUEO
            </Button>
          </DialogActions>
        </DialogAnimate>
      );
    }

    return (
      <DialogAnimate
        onClose={() => {
          dispatch(openArqueoDialogSuccess(false));
        }}
        open={openArqueoDialog}
        width="md"
      >
        {step == "OpenCaja" ? (
          <Fragment>
            <DialogTitle>
              <Typography variant="h4">Estado de caja</Typography>
            </DialogTitle>

            <DialogContent>
              <Typography variant="h6">
                La caja se encuentra actualmente cerrada. Esto le impedirá
                añadir nuevas ventas así como modificar las actuales. Si desea
                proceder deberá abrir un nuevo día de caja
              </Typography>

              <Grid container spacing={6} padding={6}>
                <Grid item xs={12} container justifyContent="center">
                  <FormControl sx={{ m: 1, minWidth: 120 }}>
                    <InputLabel id="select-arqueos-to-open-label">
                      Arqueos
                    </InputLabel>

                    <Select
                      labelId="select-arqueos-to-open-label"
                      label="Arqueos"
                      id="select-arqueos-to-open"
                      value={selectedArqueo?.id}
                      onChange={(event) => {
                        const arqueo = arqueos.find(
                          (u) => u.id == event.target.value,
                        );

                        if (arqueo) setSelectedArqueo(arqueo);
                      }}
                    >
                      {arqueos?.slice(0, 2).map((arqueo) => (
                        <MenuItem key={arqueo.id} value={arqueo.id}>
                          {getArqueoDayLabel(arqueo.start, arqueo.end)}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                {selectedArqueo?.id == todayArqueo?.id && (
                  <Grid item xs={12} sm={6} container justifyContent="center">
                    <LoadingButton
                      variant="contained"
                      pending={loading}
                      fullWidth
                      size="large"
                      color="info"
                      disabled={selectedArqueo.isOpen && selectedArqueo.isClose}
                      onClick={async () => {
                        if (!allowMakeArqueo) {
                          MyAutoSnack(
                            "error",
                            "No tienes permisos para hacer la caja. Contacta con tu administrador",
                            enqueueSnackbar,
                            closeSnackbar,
                          );

                          return;
                        }
                        try {
                          setLoading(true);
                          await axios
                            .post(`data/startArqueo/${selectedArqueo?.id}`, {
                              headers: {
                                "Content-Type": "application/json",
                                "X-Requested-With": "XMLHttpRequest",
                                "Access-Control-Allow-Origin": "*",
                              },
                            })
                            .then((response) => {
                              MyAutoSnack(
                                "success",
                                "Caja abierta correctamente",
                                enqueueSnackbar,
                                closeSnackbar,
                              );

                              dispatch(startArqueoSuccess(response.data));

                              setStep("chooseInitialArqueo");

                              setSelectedArqueo(null);
                            });
                        } catch (error) {
                          if (process.env.NODE_ENV != "production")
                            console.log(error);
                        } finally {
                          setLoading(false);
                        }
                      }}
                    >
                      {!selectedArqueo.isOpen &&
                        !selectedArqueo.isClose &&
                        "ABRIR"}
                      {selectedArqueo.isOpen &&
                        selectedArqueo.isClose &&
                        "REABRIR"}
                    </LoadingButton>
                  </Grid>
                )}
                <Grid
                  item
                  xs={12}
                  sm={selectedArqueo?.id === todayArqueo.id ? 6 : 12}
                  container
                  justifyContent="center"
                >
                  <Button
                    variant="contained"
                    color="warning"
                    fullWidth
                    size="large"
                    onClick={() => {
                      if (!accessCaja) {
                        MyAutoSnack(
                          "error",
                          "No tienes permisos para acceder a la caja. Contacta con tu administrador",
                          enqueueSnackbar,
                          closeSnackbar,
                        );

                        return;
                      }

                      dispatch(openArqueoDialogSuccess(false));
                      setSelectedArqueo(null);
                      history.push(`${PATH_APP.tuCarta.caja}`);
                    }}
                  >
                    <Typography color="white">VER ARQUEOS</Typography>
                  </Button>
                </Grid>
              </Grid>
            </DialogContent>
          </Fragment>
        ) : (
          <Fragment>
            <PopUpInputReferenceWithoutDialog
              message="Introduce la caja inicial"
              inputs={[
                {
                  target: "initialCount",
                  label: "Caja inicial",
                },
              ]}
              arqueo={arqueo}
              cajaInicial={true}
              closeFunc={() => {
                dispatch(openArqueoDialogSuccess(false));
              }}
            />
          </Fragment>
        )}

        <DialogActions>
          <Button
            color="error"
            variant="contained"
            onClick={() => {
              dispatch(openArqueoDialogSuccess(false));
            }}
          >
            CERRAR
          </Button>
        </DialogActions>
      </DialogAnimate>
    );
  };

  return (
    <ErrorComponentBoundry>
      <div className={classes.root}>
        {/* Fase 1 TPV · lease de mesa (quién está dentro de cada mesa) */}
        <MesaLeaseBridge />
        {/* Fase 4 · tiempo real del TPV (V2 `/hub/tpv` o legacy). */}
        <TpvRealtimeHubs withProducts={false} />
        {/* Stack de banners de estado — fixed, debajo del TopBar. Sólo
            montamos el wrapper si hay al menos un banner activo: antes
            estaba siempre presente con `top: 50` aunque ambos hijos
            devolvieran null, lo que dejaba un nodo fixed inútil ocupando
            z-index y obligaba a razonar como si siempre hubiera alerta.
            Ahora el árbol DOM refleja el estado real. */}
        {visibleBanners > 0 && (
          <Box
            sx={{
              position: "fixed",
              top: { xs: 45, md: 50 },
              left: 0,
              right: 0,
              zIndex: (t) => t.zIndex.appBar - 1,
              display: "flex",
              flexDirection: "column",
            }}
          >
            <SubscriptionAlertBanner />
          </Box>
        )}
        <TopBar onOpenNav={() => setOpenNav(true)} />
        <NavBar onCloseNav={() => setOpenNav(false)} isOpenNav={openNav} />

        {/* Auto-aplicador post-Stripe Checkout: si el user vuelve del
            checkout del wizard con add-ons pendientes en localStorage,
            los aplica de golpe y marca `checkout` como completado. */}
        <PostCheckoutAutoApply />

        {open && !openClockingModal && (
          <ModalEmployee isOpen={open} onClose={() => setOpen(false)} />
        )}

        {openClockingModal && <EmployeeShiftTracker />}
        {kioskOpen && <KioskOverlay />}
        {/* Ayuda integrada: «?» discreto; el panel se carga al pulsar. */}
        <HelpLauncher />
        {/* Admin › Versiones: órdenes remotas a este dispositivo y aviso de
            versión nueva (solo aquí, nunca dentro de una mesa). */}
        <DeviceCommandsBridge />
        <NewVersionBanner />
        {/* Recordatorio de firmas pendientes — se abre tras la
            identificación del empleado en el kiosk si tiene adjustments
            PENDING. Solo aparece cuando NO está abierto el kiosk (para no
            taparlo) y hay un empleado normal identificado (no admin). */}
        {!kioskOpen
          && pendingSignatures?.length > 0
          && currentActiveEmployee
          && currentActiveEmployee !== 0 && (
            <PendingSignaturesModal employeeId={currentActiveEmployee} />
          )}
        <div
          className={classes.main}
          // marginTop inline ADDS al paddingTop que define classes.main por breakpoint.
          // Una approach responsive con inline-px sería demasiado acoplada; margin
          // es aditivo y funciona con el flex layout del root.
          style={
            extraPaddingTop > 0 ? { marginTop: extraPaddingTop } : undefined
          }
        >
          <WhatsAppQuotaBanner />
          {children}
        </div>

        {haveErrorLogs && RenderDialogAnimate()}
        {openSession && RenderCloseActiveSessions()}
        {openArqueoDialog && RenderArqueoCloseOpenDialog()}

        {/* Onboarding legal restrictivo. Si hay documentos pendientes de firma,
            bloquea toda la app con un modal — sin escape ni cancel. Vive aquí
            para que aplique a todos los usuarios autenticados que pasen por el
            DashboardLayout (admins de restaurante, franquiciados, etc.). */}
        <LegalOnboardingGate />
        {/* F64 · Modal global de contratación de add-ons (disparado
            desde nav CTAs "Contrátalo"). */}
        <AddOnContractDialog />
      </div>
    </ErrorComponentBoundry>
  );
}

export default DashboardLayout;
